import {useNavigation} from '@react-navigation/native';
import {MoodLightDto} from 'puffco-api-axios-client';
import React from 'react';
import {useSelector} from 'react-redux';

import {Alert} from '../../components/Alert';
import {Constants} from '../../constants';
import {Alerts, Strings, interpolate} from '../../constants/Strings';
import {Screens} from '../../constants/navigation';
import type {
  RootStackParamList,
  RootStackScreenProps,
} from '../../navigators/RootStackNavigator';
import {toMoodLightLibrary} from '../../navigators/util';
import {analytics} from '../../services/analytics';
import {getAppUrl} from '../../util/url';
import {shareApi} from '../api/apis';
import {useUserVerifiedGuard} from '../hooks/useUserVerifiedGuard';
import {allAndCurrentDeviceMoodLightsSelector} from '../redux/slices/moodLightSlice';
import {userSelector} from '../redux/slices/userSlice';
import {MoodType} from '../types';
import {shareUrl} from '../utils/shareUrl';
import {useAsyncFn} from './useAsyncFn';

type Navigation = RootStackScreenProps<keyof RootStackParamList>['navigation'];

interface ShareMoodLightByIdOptions {
  shareId: string;
  moodlightId: string;
  moodlightName: string;
}

export const useShareMoodLight = () => {
  const user = useSelector(userSelector);
  const moodLights = useSelector(allAndCurrentDeviceMoodLightsSelector);

  const {navigate} = useNavigation<Navigation>();
  const {isUserVerified} = useUserVerifiedGuard();

  const [shareableMoodLight, createSharedMoodLight] = useAsyncFn(
    async (moodLight: MoodLightDto) =>
      shareApi
        .createShareMoodLightId({
          moodLightCreateShareDto: {
            category: moodLight.category,
            id: moodLight.id,
            modified: moodLight.modified,
            name: moodLight.name,
            colors: moodLight.projector.meta.userColors ?? [],
            type: moodLight.projector.meta.moodType ?? MoodType.NO_ANIMATION,
            tempo: moodLight.projector.meta.tempoFrac ?? 0,
            originalMoodLightId: moodLight.projector.meta.originalMoodLightId!,
            led3Meta: {
              ...moodLight.projector.meta,
              userColors: moodLight.projector.meta.userColors ?? [],
            },
          },
        })
        .then(r => ({...r, data: {...r.data, moodlightId: moodLight.id}})),
    [],
  );

  const shareMoodLightById = React.useCallback(
    async ({
      shareId,
      moodlightId,
      moodlightName,
    }: ShareMoodLightByIdOptions) => {
      const url = `${getAppUrl()}/${Constants.SHARE_PATH}/${shareId}`;

      await shareUrl({url})
        .catch(() => void 0)
        .then(() => {
          analytics.trackEvent('share', {
            content_type: 'moodlight',
            item_id: moodlightId,
            item_name: moodlightName,
          });
        });
    },
    [],
  );

  React.useEffect(() => {
    if (shareableMoodLight.error) {
      Alert.alert(Alerts.MOOD_LIGHT_SHARE_ERROR_TITLE);

      return;
    }

    if (!shareableMoodLight.value) return;

    const {
      shareMoodLightId: shareId,
      moodLightName: moodlightName,
      moodlightId,
    } = shareableMoodLight.value.data;

    const profileName = moodlightName || 'Mood Profile';

    Alert.alert(
      interpolate(Alerts.SHARE_PROFILE_READY_TITLE, {profileName}),
      interpolate(Alerts.SHARE_PROFILE_READY_BODY, {profileName}),
      [
        {
          style: 'cancel',
          text: Strings.CANCEL,
        },
        {
          onPress: () =>
            shareMoodLightById({shareId, moodlightId, moodlightName}).catch(
              () => void 0,
            ),
          text: Strings.SHARE,
        },
      ],
    );
  }, [shareableMoodLight.error, shareableMoodLight.value, shareMoodLightById]);

  const onLoginClick = React.useCallback(() => {
    navigate(Screens.Login, {
      redirect: toMoodLightLibrary().encode(),
    });
  }, [navigate]);

  const share = React.useCallback(
    async (id: string) => {
      if (!user) {
        onRequireLogin(
          Alerts.ACCESS_DENIED,
          Alerts.FEATURE_ACCESS_MESSAGE,
          onLoginClick,
        );

        return;
      }

      if (!isUserVerified()) return;

      const moodLight = moodLights[id];

      if (!moodLight) return;

      await createSharedMoodLight(moodLight);
    },
    [user, moodLights, onLoginClick, isUserVerified, createSharedMoodLight],
  );

  return React.useMemo(
    () => [shareableMoodLight, share] as const,
    [shareableMoodLight, share],
  );
};

const onRequireLogin = (title: string, message: string, onLogin: () => void) =>
  Alert.alert(
    title,
    message,
    [
      {
        text: Strings.LOG_IN,
        onPress: () => onLogin(),
        style: 'default',
      },
      {text: Strings.CANCEL, style: 'cancel'},
    ],
    {cancelable: false},
  );
